import * as React from 'react';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MatchInfoDto from '../models/dtos/MatchInfoDto';
import SportEntityDto from '../models/dtos/SportEntityDto';

const matchLinkStyles = {
    color: 'primary.main',
    textDecoration: 'none',
    minWidth: 0,
    '&:hover': {
        textDecoration: 'underline',
    },
};

const matchStateLabels: Record<number, string> = {
    1: 'SCHEDULED',
    2: 'LIVE',
    3: 'FINISHED',
    4: 'PROCESSING',
    5: 'CANCELLED',
};

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function teamName(team?: SportEntityDto | null) {
    return team?.name ?? '-';
}

function teamLogo(team?: SportEntityDto | null) {
    return team?.photo_url ?? (team as any)?.photoUrl ?? undefined;
}

function matchHomeTeam(match: MatchInfoDto): SportEntityDto | null {
    return (match.home_team ?? (match as any).homeTeam ?? null) as SportEntityDto | null;
}

function matchAwayTeam(match: MatchInfoDto): SportEntityDto | null {
    return (match.away_team ?? (match as any).awayTeam ?? null) as SportEntityDto | null;
}

function matchState(match: MatchInfoDto): number | null {
    const state = match.matchState ?? (match as any).state;
    return Number.isFinite(Number(state)) ? Number(state) : null;
}

function matchStateLabel(match: MatchInfoDto) {
    const state = matchState(match);
    if (state == null) return 'UNKNOWN';
    return matchStateLabels[state] ?? `STATE ${state}`;
}

function matchScore(match: MatchInfoDto) {
    const state = matchState(match);
    if (state === 1 || state === 5) return null;
    const homeScore = match.score_home ?? (match as any).scoreHome;
    const awayScore = match.score_away ?? (match as any).scoreAway;
    if (homeScore == null || awayScore == null) return null;
    return `${homeScore} - ${awayScore}`;
}

interface PrivateClubMatchSummaryProps {
    matchId: number;
    match: MatchInfoDto | null | undefined;
    link?: boolean;
    newTab?: boolean;
    showStats?: boolean;
}

export default function PrivateClubMatchSummary(props: PrivateClubMatchSummaryProps) {
    const matchUrl = `/matches/${props.matchId}`;
    const renderText = (label: string) => {
        if (!props.link) {
            return <Typography component="span" variant="body2" noWrap>{label}</Typography>;
        }
        if (props.newTab === false) {
            return (
                <Box component={RouterLink} to={matchUrl} sx={matchLinkStyles}>
                    <Typography component="span" variant="body2" noWrap>{label}</Typography>
                </Box>
            );
        }
        return (
            <Box component="a" href={matchUrl} target="_blank" rel="noreferrer" sx={matchLinkStyles}>
                <Typography component="span" variant="body2" noWrap>{label}</Typography>
            </Box>
        );
    };

    if (props.match === undefined) {
        return (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                {renderText(`Match ${props.matchId}`)}
                <Typography variant="caption" color="text.secondary">Loading match details...</Typography>
            </Stack>
        );
    }

    if (props.match === null) {
        return (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                {renderText(`Match ${props.matchId}`)}
                <Typography variant="caption" color="error">Details unavailable</Typography>
            </Stack>
        );
    }

    const homeTeam = matchHomeTeam(props.match);
    const awayTeam = matchAwayTeam(props.match);
    const score = matchScore(props.match);
    const betsCount = props.match.betsCount ?? (props.match as any).bets ?? null;
    const viewsCount = props.match.viewsCount ?? (props.match as any).views ?? null;

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
            <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar src={teamLogo(homeTeam)} sx={{ width: 28, height: 28 }} />
                {renderText(teamName(homeTeam))}
                <Typography variant="body2" color="text.secondary">vs</Typography>
                <Avatar src={teamLogo(awayTeam)} sx={{ width: 28, height: 28 }} />
                {renderText(teamName(awayTeam))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatDate(props.match.date)}
            </Typography>
            <Chip size="small" variant="outlined" label={matchStateLabel(props.match)} />
            {score && <Chip size="small" label={score} />}
            {props.showStats && betsCount != null && <Chip size="small" variant="outlined" label={`${betsCount} bets`} />}
            {props.showStats && viewsCount != null && <Chip size="small" variant="outlined" label={`${viewsCount} views`} />}
        </Stack>
    );
}
